import { MoneyText } from "./money-text";
import * as PIXI from "pixi.js";

import { Vector } from "./vector";
import { Point } from "../models";

import { CurrencyPipe } from "../../../shared/utils/currency.pipe";
import { CurrencyDeviderPipe } from "../../../shared/utils/currency-devider.pipe";
import { InterTightWeight } from "../../../shared/utils/inter-tight-weights";
import { Table } from "./table";
import { CurrencyInfo } from "../../../shared/models/currency-info";

type ChipDesignLayout = "simple" | "realistic";

const SIMPLE_CHIP_SHADOW_PX = 8;
const REALISTIC_CHIP_SIZE = 64 * 0.55;
const MONEY_GAP = 9;

const ChipDesignLayouts: Record<
  ChipDesignLayout,
  { chip: Point; convertedMoney: Point }
> = {
  simple: {
    chip: { x: -SIMPLE_CHIP_SHADOW_PX, y: 0 },
    convertedMoney: { x: 12, y: 30 },
  },
  realistic: {
    chip: { x: 0, y: 0 },
    convertedMoney: { x: 55, y: 11 },
  },
};

function makeMoneyTextStyle(fill: string) {
  return new PIXI.TextStyle({
    fontFamily: "InterTight",
    fontSize: 27,
    fontWeight: InterTightWeight.Medium,
    fill,
    align: "center",
  });
}

type PotAmountItem = {
  amount: number;
  howManyTimes: number;
  label: string;
};

const availableAmounts = [
  { amount: 100000000, label: "100m" }, // 100m
  { amount: 10000000, label: "10m" }, // 10m
  { amount: 5000000, label: "5m" }, // 5m
  { amount: 1000000, label: "1m" }, // 1m
  { amount: 500000, label: "500k" }, // 500k
  { amount: 100000, label: "100k" }, // 100k
  { amount: 50000, label: "50k" }, // 50k
  { amount: 10000, label: "10k" }, // 10k
  { amount: 5000, label: "5k" }, // 5k
  { amount: 1000, label: "1k" }, // 1k
  { amount: 500, label: "500" }, // 500
  { amount: 100, label: "100" }, // 100
  { amount: 50, label: "50" }, // 50
  { amount: 10, label: "10" }, // 10
  { amount: 5, label: "5" }, // 5
  { amount: 1, label: "1" }, // 1
  { amount: 0.5, label: "50c" }, // 50c
  { amount: 0.1, label: "10c" }, // 10c
  { amount: 0.05, label: "5c" }, // 5c
  { amount: 0.01, label: "1c" }, // 1c
];

export class Pot {
  container;
  private moneyTextStyle = makeMoneyTextStyle("#ffffff");
  private convertedMoneyTextStyle = makeMoneyTextStyle("#ffffff");
  private chipsSprite: PIXI.Sprite;
  private chipsContainer = new PIXI.Container();
  potId: number; // this will be playerId if pot belongs to player
  private potAmount: MoneyText;
  private potBackground: PIXI.Graphics;
  private amountContainer: PIXI.Container;
  private convertedPotAmount: PIXI.Text;
  amount: number;
  shouldMove = false;
  targetPosition: Point;
  private movingSpeed = 0;
  private movingDirection: Point;
  defaultPosition: Point = { x: 0, y: 0 };
  private currentPosition: Point = { x: 0, y: 0 };
  private timeToTraget = 0.5; // traveling time
  private oldDistance = 9999;
  isSplit;
  rakeAmount = 0;
  currency: CurrencyInfo;
  seatNumber: number;
  currentPotItems: PotAmountItem[] = [];
  currentPotSprites: PIXI.Sprite[] = [];

  private bigBlind: number;
  private stacksInBB: boolean;
  newChipsDesign: boolean = false;
  showPotWithRakeAmount: boolean;

  constructor(
    textures,
    potId: number,
    position: Point,
    amount: number,
    currency: CurrencyInfo,
    seatNumber: number,

    bigBlind: number,
    stacksInBB: boolean,
    showPotWithRakeAmount: boolean,
    isSplit?: boolean,

    public table?: Table,
    newChipsDesign?: boolean,
  ) {
    this.bigBlind = bigBlind;
    this.stacksInBB = stacksInBB;

    this.seatNumber = seatNumber;
    this.container = new PIXI.Container();
    this.newChipsDesign = newChipsDesign;
    this.updatePosition(position);

    this.chipsSprite = new PIXI.Sprite(textures["chip"].texture);
    // this.container.addChild(this.chipsSprite);

    if (amount === undefined) {
      amount = 0;
    }
    this.amount = amount;

    this.potAmount = new MoneyText(
      currency
        ? CurrencyPipe.prototype.transform(
            CurrencyDeviderPipe.prototype.transform(this.amount, currency),
            currency,
          )
        : `${this.amount}`,
      this.moneyTextStyle,
    );

    this.convertedPotAmount = new PIXI.Text(
      this.currency
        ? CurrencyPipe.prototype.transform(
            CurrencyDeviderPipe.prototype.transform(this.amount, currency),
            currency,
          )
        : `${this.amount}`,
      this.convertedMoneyTextStyle,
    );
    this.container.addChild(this.convertedPotAmount);
    this.amountContainer = new PIXI.Container();
    this.potBackground = new PIXI.Graphics();
    this.amountContainer.addChild(this.potBackground);
    this.amountContainer.addChild(this.potAmount);
    this.drawPotBackground();
    this.container.addChild(this.chipsContainer);
    this.container.addChild(this.amountContainer);
    this.convertedPotAmount.visible = false; // TODO: show/hide if skin standard/advanced
    this.setPositions();

    if (this.amount === 0) {
      this.hidePot();
    }

    this.isSplit = isSplit;
    this.currency = currency;
    this.showPotWithRakeAmount = showPotWithRakeAmount;
    this.updateChipsSprites();
  }
  setNewChipsDesign(newChipsDesign: boolean) {
    this.newChipsDesign = newChipsDesign;
    this.setPositions();
    this.updateChipsSprites();
  }

  private get layoutOffsets() {
    return ChipDesignLayouts[this.newChipsDesign ? "realistic" : "simple"];
  }

  update(dt) {
    if (this.shouldMove) {
      this.move(dt);
    }
  }

  updatePosition(position: Point) {
    this.defaultPosition = { x: position.x, y: position.y };
    this.currentPosition = { x: position.x, y: position.y };
    this.container.position.set(position.x, position.y);
  }

  private setPositions() {
    this.layoutChipLeftMoneyRight();
    const { convertedMoney } = this.layoutOffsets;
    this.convertedPotAmount.position.set(convertedMoney.x, convertedMoney.y);
    this.convertedPotAmount.anchor.set(this.newChipsDesign ? 1 : 0, 0.5);
  }

  hidePot() {
    this.container.visible = false;
  }

  showPot() {
    this.container.visible = true;
  }

  private getContentBounds(): PIXI.Rectangle {
    const bounds = this.container.getLocalBounds().clone();
    if (!this.newChipsDesign) {
      bounds.x += SIMPLE_CHIP_SHADOW_PX;
      bounds.width -= SIMPLE_CHIP_SHADOW_PX;
    }
    return bounds;
  }

  private getChipsBounds(): PIXI.Rectangle | null {
    const bounds = this.chipsContainer.getLocalBounds().clone();
    if (!bounds.width || !bounds.height) {
      return null;
    }
    if (!this.newChipsDesign) {
      bounds.x += SIMPLE_CHIP_SHADOW_PX;
      bounds.width -= SIMPLE_CHIP_SHADOW_PX * 2;
    }
    return bounds;
  }

  private alignRight = false;

  setAlignRight(alignRight: boolean) {
    this.alignRight = alignRight;
    this.syncContainerPivotToContentCenter();
  }

  private syncContainerPivotToContentCenter() {
    const b = this.getContentBounds();
    if (!b.width || !b.height) {
      return;
    }
    this.container.pivot.set(
      this.alignRight ? b.x + b.width : b.x + b.width / 2,
      b.y + b.height / 2 - this.getAwayFromPlayerOffset(),
    );
  }

  private getAwayFromPlayerOffset(): number {
    const center = this.table?.currentMainPotPositions?.[0];
    const chips = this.getChipsBounds();
    if (!this.newChipsDesign || this.seatNumber === 99 || !center || !chips) {
      return 0;
    }
    const extraHeight = Math.max(0, chips.height - REALISTIC_CHIP_SIZE);
    return (Math.sign(center.y - this.defaultPosition.y) * extraHeight) / 2;
  }

  updateBigBlind(bigBlind: number) {
    this.bigBlind = bigBlind;
    this.updateAmount(this.amount);
  }

  updateStacksInBB(stacksInBB: boolean) {
    this.stacksInBB = stacksInBB;
    this.updateAmount(this.amount);
  }

  updateAmount(newAmount: number, rakeAmount?: number) {
    this.amount = newAmount;

    if (newAmount === undefined) {
      newAmount = 0;
    }

    if (this.showPotWithRakeAmount) {
      if (rakeAmount) {
        if (newAmount > 0) {
          this.rakeAmount = rakeAmount;
          newAmount += rakeAmount;
        }
      }
      //  else if (newAmount > 0) {
      //     newAmount += this.rakeAmount;
      // }
    }

    let newAmountConverted = 0;
    if (this.stacksInBB) {
      const amount = newAmount / this.bigBlind;
      newAmountConverted = Math.round((amount + Number.EPSILON) * 100) / 100;
    } else {
      newAmountConverted = this.currency
        ? CurrencyDeviderPipe.prototype.transform(newAmount, this.currency)
        : newAmount;
    }

    if (this.stacksInBB) {
      this.potAmount.text = `${newAmountConverted} BB`;
    } else {
      this.potAmount.text = this.currency
        ? CurrencyPipe.prototype.transform(newAmountConverted, this.currency)
        : `${newAmountConverted}`;
    }

    if (newAmount === 0) {
      this.rakeAmount = 0;
    }
    this.updateChipsSprites();
    this.refreshBackgroud();
    this.resetPot();
  }
  private drawPotBackground() {
    const bgWidth = this.potAmount.width + 12;
    const bgHeight = this.potAmount.height + 4;
    this.potBackground.clear();
    this.potBackground.beginFill(0x000000, 0.75);
    this.potBackground.drawRoundedRect(0, 0, bgWidth, bgHeight, 8);
    this.potBackground.endFill();
    this.potAmount.position.set(8, 2);
    this.amountContainer.pivot.set(bgWidth / 2, bgHeight / 2);
  }

  refreshBackgroud() {
    this.drawPotBackground();
    this.layoutChipLeftMoneyRight();
  }
  revertToOriginalPosition(newChipDesign: boolean = false) {
    this.updateChipsSprites();
  }

  move(dt) {
    this.currentPosition.x +=
      (dt / 1000) * this.movingSpeed * this.movingDirection.x;
    this.currentPosition.y +=
      (dt / 1000) * this.movingSpeed * this.movingDirection.y;
    const newDistance = Vector.prototype.distace(
      this.targetPosition,
      this.currentPosition,
    );
    if (this.oldDistance <= newDistance) {
      // this.shouldMove = false;
      this.amount = 0; // TODO: check if this is working
      this.resetPot();
      // this.hidePot();
      // this.currentPosition = { x: this.defaultPosition.x, y: this.defaultPosition.y };

      if (this.isSplit) {
        this.selfDistruct();
      }

      return;
    }
    this.container.position.set(this.currentPosition.x, this.currentPosition.y);
    this.oldDistance = newDistance;
  }

  resetPot() {
    this.shouldMove = false;
    this.currentPosition = {
      x: this.defaultPosition.x,
      y: this.defaultPosition.y,
    };
    this.container.position.set(this.currentPosition.x, this.currentPosition.y);
    this.oldDistance = 9999;
    if (this.amount === 0) {
      this.hidePot();
    } else {
      this.showPot();
    }
  }

  moveToTarget(targetPosition: Point) {
    if (!this.showPotWithRakeAmount) {
      if (this.rakeAmount > 0) {
        this.amount -= this.rakeAmount;
        this.rakeAmount = 0;
        const newAmountConverted = this.currency
          ? CurrencyDeviderPipe.prototype.transform(this.amount, this.currency)
          : this.amount;
        this.potAmount.text = this.currency
          ? CurrencyPipe.prototype.transform(newAmountConverted, this.currency)
          : `${newAmountConverted}`;
      }
    }
    this.resetPot();
    this.targetPosition = targetPosition;
    const distanceToTarget = Vector.prototype.distace(
      targetPosition,
      this.currentPosition,
    );
    this.movingSpeed = distanceToTarget / this.timeToTraget;
    this.movingDirection = Vector.prototype.direction(
      targetPosition,
      this.currentPosition,
    );
    this.shouldMove = true;
  }

  isUpSided(seatPosition: number) {
    if ((seatPosition >= 0 && seatPosition < 4) || seatPosition === 9) {
      return false;
    } else {
      return true;
    }
  }

  selfDistruct() {
    this.container.parent.removeChild(this.container);
  }

  rearngeElements(position: Point) {
    this.updatePosition(position);
    this.setPositions();
  }

  getCurrencyBreakdown(amount: number): PotAmountItem[] {
    const result: PotAmountItem[] = [];

    for (const amountItem of availableAmounts) {
      if (amount >= amountItem.amount) {
        const howManyTimes = Math.floor(
          this.roundToTwo(
            this.roundToTwo(amount) / this.roundToTwo(amountItem.amount),
          ),
        );
        if (howManyTimes >= 0) {
          result.push({
            amount: amountItem.amount,
            howManyTimes,
            label: amountItem.label,
          });
          amount = this.roundToTwo(
            amount - this.roundToTwo(howManyTimes * amountItem.amount),
          );
        }
      }
      if (result.length >= 5) break;
    }

    return result;
  }

  roundToTwo(num: number): number {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  }

  createChipsSprites(potItems: PotAmountItem[]): PIXI.Sprite[] {
    return potItems.map((item, index) => {
      const imageUrl = `assets/chips/chip${item.label}x${Math.min(item.howManyTimes, 4)}.png`;
      const texture = PIXI.Texture.from(imageUrl);
      const chipSprite = new PIXI.Sprite(texture);
      chipSprite.zIndex = potItems.length - index;
      chipSprite.scale.set(0.55);
      if (!texture.baseTexture.valid) {
        texture.baseTexture.once("loaded", () => this.relayoutLoadedChips());
        texture.baseTexture.once("error", () =>
          console.error(`Pot: failed to load chip texture (${imageUrl})`),
        );
      }
      return chipSprite;
    });
  }

  private relayoutLoadedChips() {
    if (!this.newChipsDesign) {
      return;
    }
    this.setPotsPosition();
    this.refreshBackgroud();
  }
  private layoutChipLeftMoneyRight() {
    const chips = this.getChipsBounds();
    if (!chips) {
      return;
    }
    const money = this.amountContainer;
    const moneyCenterY = chips.y + chips.height / 2;
    money.position.set(chips.right + MONEY_GAP + money.width / 2, moneyCenterY);
    this.syncContainerPivotToContentCenter();
  }

  updateChipsSprites(): void {
    this.chipsContainer.removeChildren();
    if (this.newChipsDesign) {
      this.currentPotItems = this.getCurrencyBreakdown(
        this.currency
          ? CurrencyDeviderPipe.prototype.transform(this.amount, this.currency)
          : this.amount,
      );
      this.currentPotSprites = this.createChipsSprites(this.currentPotItems);
      this.setPotsPosition();
    } else {
      this.chipsContainer.addChild(this.chipsSprite);
      const { chip } = this.layoutOffsets;
      this.chipsSprite.position.set(chip.x, chip.y);
    }
    this.refreshBackgroud();
  }

  round(value: number, decimals = 2): number {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  setPotsPosition() {
    const numOfPots = this.currentPotItems.length;
    var xPos = [0, 0, 0, 0, 0];
    var yPos = [0, 0, 0, 0, 0];
    const { chip } = this.layoutOffsets;
    const standardX = chip.x;
    const standardY = chip.y;
    const stackOffset = (index: number) => {
      const sprite = this.currentPotSprites[index];
      return sprite.texture.baseTexture.valid
        ? sprite.height - REALISTIC_CHIP_SIZE
        : (this.currentPotItems[index].howManyTimes - 1) * 6;
    };
    const col = REALISTIC_CHIP_SIZE;
    const layout: Point[] = [
      [{ x: 0, y: 0 }],
      [
        { x: 0, y: 15 },
        { x: 0, y: -15 },
      ],
      [
        { x: col / 2, y: 15 },
        { x: -col / 2, y: 0 },
        { x: col / 2, y: -15 },
      ],
      [
        { x: -col / 2, y: 15 },
        { x: col / 2, y: 15 },
        { x: -col / 2, y: -15 },
        { x: col / 2, y: -15 },
      ],
      [
        { x: 0, y: 15 },
        { x: col, y: 15 },
        { x: -col, y: 0 },
        { x: 0, y: -15 },
        { x: col, y: -15 },
      ],
    ][numOfPots - 1];
    if (!layout) {
      return;
    }
    layout.forEach((point, i) => {
      xPos[i] = standardX + point.x;
      yPos[i] = standardY + point.y - stackOffset(i);
    });
    this.currentPotSprites.slice().reverse().forEach((item, i) => {
      item.position.set(
        xPos[this.currentPotItems.length - i - 1],
        yPos[this.currentPotItems.length - i - 1],
      );
      this.chipsContainer.addChild(item);
    });
  }
}
